// Inspector of the selected flow node: trigger, conditions or one step, edited with the same pickers and filter builder as views.

import { useState } from 'react';
import type { AutomationSpec, FieldDef, Id, Step, Trigger } from '@confdeck/core-shared';
import { TermPicker, UserPicker } from '../fields/pickers.tsx';
import { useMeta } from '../sync/react.tsx';
import { FilterBuilder, fromFlat, toFlat } from '../table/FilterBuilder.tsx';
import { FIELD_ICONS } from '../ui/icons.tsx';
import { Button, Chip, Input, Menu, MenuContent, MenuItem, MenuTrigger, Popover, PopoverContent, PopoverTrigger, Segmented, Select } from '../ui/primitives.tsx';
import { prettyTemplate } from './describe.ts';
import { useMailTemplates } from './mail-templates.ts';
import s from './automations.module.css';

export type Selection = { kind: 'trigger' } | { kind: 'conditions' } | { kind: 'step'; path: number[] };

/** Path: [top index] or [top, side(0 then / 1 else), inner index, …]. */
export function getStep(steps: Step[], path: number[]): Step | undefined {
  const st = steps[path[0]!];
  if (path.length === 1 || !st || st.type !== 'branch') return st;
  return getStep(path[1] === 0 ? st.then : st.else, path.slice(2));
}
export function setStep(steps: Step[], path: number[], next: Step | null): Step[] {
  const i = path[0]!;
  if (path.length === 1) return next ? steps.map((x, j) => (j === i ? next : x)) : steps.filter((_, j) => j !== i);
  return steps.map((x, j) => {
    if (j !== i || x.type !== 'branch') return x;
    const side = path[1] === 0 ? 'then' : 'else';
    return { ...x, [side]: setStep(x[side], path.slice(2), next) };
  });
}

const TRIGGERS: { type: Trigger['type']; label: string }[] = [
  { type: 'record.created', label: 'Создана запись' }, { type: 'record.updated', label: 'Изменилось поле' }, { type: 'date', label: 'Относительно даты' },
];

export function Inspector({ draft, setDraft, sel, canEdit, onRemoved }: { draft: AutomationSpec; setDraft: (d: AutomationSpec) => void; sel: Selection; canEdit: boolean; onRemoved: () => void }) {
  const meta = useMeta();
  const typeId = 'typeId' in draft.trigger ? draft.trigger.typeId : undefined;
  const body = (() => {
    if (sel.kind === 'trigger') return <TriggerEditor t={draft.trigger} onChange={(trigger) => setDraft({ ...draft, trigger, conditions: trigger.type !== draft.trigger.type || ('typeId' in trigger && trigger.typeId !== typeId) ? null : draft.conditions })} />;
    if (sel.kind === 'conditions') {
      if (!typeId) return <p className={s.hint}>Условия проверяются по записи из запуска.</p>;
      return (
        <>
          <p className={s.hint}>Сценарий идёт дальше, только если запись подходит. Условия — тот же язык, что у фильтров таблиц.</p>
          <FilterBuilder typeId={typeId} value={toFlat(draft.conditions)} onChange={(ff) => setDraft({ ...draft, conditions: fromFlat(ff) })} />
        </>
      );
    }
    const st = getStep(draft.steps, sel.path);
    if (!st) return null;
    const put = (n: Step | null) => { setDraft({ ...draft, steps: setStep(draft.steps, sel.path, n) }); if (!n) onRemoved(); };
    return (
      <>
        <StepEditor st={st} typeId={typeId} onChange={put} />
        {canEdit && <Button size="sm" icon="trash" variant="danger" onClick={() => put(null)}>Удалить шаг</Button>}
      </>
    );
  })();
  return (
    <aside className={s.inspector} aria-label="Настройка шага">
      <fieldset disabled={!canEdit} className={s.fieldset}>{body}</fieldset>
      {meta && null}
    </aside>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className={s.row}><span className={s.rowLabel}>{label}</span><span className={s.rowValue}>{children}</span></label>;
}

function TypeSelect({ value, onChange }: { value?: Id; onChange: (id: Id) => void }) {
  const meta = useMeta();
  return (
    <Menu>
      <MenuTrigger asChild><Button size="sm" variant="secondary" caret>{meta.type(value)?.nameMany ?? 'Раздел'}</Button></MenuTrigger>
      <MenuContent>{meta.types().map((t) => <MenuItem key={t.id} icon={t.icon} onSelect={() => onChange(t.id)}>{t.nameMany}</MenuItem>)}</MenuContent>
    </Menu>
  );
}

function FieldSelect({ typeId, value, onChange, filter }: { typeId: Id; value?: string; onChange: (key: string) => void; filter?: (f: FieldDef) => boolean }) {
  const meta = useMeta();
  const fields = meta.fields(typeId).filter((f) => !filter || filter(f));
  return (
    <Menu>
      <MenuTrigger asChild><Button size="sm" variant="secondary" caret>{meta.field(typeId, value ?? '')?.name ?? 'Поле'}</Button></MenuTrigger>
      <MenuContent>{fields.map((f) => <MenuItem key={f.id} icon={FIELD_ICONS[f.fieldType]} onSelect={() => onChange(f.key)}>{f.name}</MenuItem>)}</MenuContent>
    </Menu>
  );
}

function TriggerEditor({ t, onChange }: { t: Trigger; onChange: (t: Trigger) => void }) {
  const meta = useMeta();
  const typeId = 'typeId' in t ? t.typeId : meta.types()[0]!.id;
  const setType = (type: Trigger['type']) => {
    const firstDate = meta.fields(typeId!).find((f) => f.fieldType === 'date')?.key ?? 'createdAt';
    const firstTax = meta.fields(typeId!).find((f) => f.fieldType === 'taxonomy')?.key ?? 'name';
    if (type === 'record.created') onChange({ type, typeId: typeId! });
    if (type === 'record.updated') onChange({ type, typeId: typeId!, field: firstTax });
    if (type === 'date') onChange({ type, typeId: typeId!, field: firstDate, offsetDays: -1 });
  };
  const field = 'field' in t ? meta.field(t.typeId, t.field) : undefined;
  // A form's scenario starts from its form; which form is fixed — the scenario belongs to it (R-F3).
  if (t.type === 'form.submitted') return (
    <>
      <Row label="Что запускает"><span>Новый ответ формы «{meta.form(t.formId)?.name ?? 'форма удалена'}»</span></Row>
      <p className={s.hint}>Сценарий принадлежит форме. Форму, её поля и публикацию меняют в разделе «Формы».</p>
    </>
  );
  return (
    <>
      <Row label="Что запускает">
        <Select size="sm" label="Что запускает" value={t.type} onChange={(v) => setType(v as Trigger['type'])} options={TRIGGERS.map((x) => ({ value: x.type, label: x.label }))} />
      </Row>
      {'typeId' in t && <Row label="Раздел"><TypeSelect value={t.typeId} onChange={(id) => onChange(t.type === 'record.created' ? { type: t.type, typeId: id } : t.type === 'date' ? { ...t, typeId: id, field: meta.fields(id).find((f) => f.fieldType === 'date')?.key ?? '' } : { ...t, typeId: id, field: meta.fields(id)[0]?.key ?? '' } as Trigger)} /></Row>}
      {t.type === 'record.updated' && (
        <>
          <Row label="Поле"><FieldSelect typeId={t.typeId} value={t.field} filter={(f) => f.fieldType !== 'relation'} onChange={(k) => onChange({ ...t, field: k, to: undefined })} /></Row>
          {field?.fieldType === 'taxonomy' && (
            <Row label="Новое значение">
              <Popover>
                <PopoverTrigger asChild><Button size="sm" variant="secondary">{(t.to ?? []).length ? (t.to ?? []).map((v) => meta.term(String(v))?.name).join(', ') : 'любое'}</Button></PopoverTrigger>
                <PopoverContent width={280}><TermPicker field={{ ...field, options: { ...field.options, multiple: true } }} value={t.to ?? []} onChange={(v) => onChange({ ...t, to: v as unknown[] })} /></PopoverContent>
              </Popover>
            </Row>
          )}
        </>
      )}
      {t.type === 'date' && (
        <>
          <Row label="Дата"><FieldSelect typeId={t.typeId} value={t.field} filter={(f) => f.fieldType === 'date'} onChange={(k) => onChange({ ...t, field: k })} /></Row>
          <Row label="Сдвиг, дней"><Input type="number" value={t.offsetDays} className={s.numInput} onChange={(e) => onChange({ ...t, offsetDays: Number(e.target.value) })} /></Row>
          <p className={s.hint}>−3 — за три дня до даты, 0 — в тот же день, 2 — через два дня после.</p>
        </>
      )}
    </>
  );
}

const TOKENS = [
  { label: 'название', v: (titleField: string) => `{{record.data.${titleField}}}` },
  { label: 'ответственный', v: () => '{{record.ownerId}}' },
  { label: 'эта запись', v: () => '{{record.id}}' },
  { label: 'сегодня +3', v: () => '{{today+3}}' },
];

/** A value: plain text with template tokens, or a picker when the target field is a term or a user. */
function ValueEditor({ field, value, onChange, sourceTypeId }: { field?: FieldDef; value: unknown; onChange: (v: unknown) => void; sourceTypeId?: Id }) {
  const meta = useMeta();
  const titleField = sourceTypeId ? meta.type(sourceTypeId)?.titleField ?? 'name' : 'name';
  if (field?.fieldType === 'taxonomy') {
    return (
      <Popover>
        <PopoverTrigger asChild><Button size="sm" variant="secondary">{prettyTemplate(value, meta, sourceTypeId) || 'выбрать'}</Button></PopoverTrigger>
        <PopoverContent width={280}><TermPicker field={field} value={value} onChange={onChange} /></PopoverContent>
      </Popover>
    );
  }
  if (field?.fieldType === 'user') {
    return (
      <span className={s.opts}>
        <Button size="sm" variant={value === '{{record.ownerId}}' ? 'secondary' : 'ghost'} onClick={() => onChange('{{record.ownerId}}')}>ответственный записи</Button>
        <Popover>
          <PopoverTrigger asChild><Button size="sm" variant={typeof value === 'string' && !value.startsWith('{{') ? 'secondary' : 'ghost'}>{typeof value === 'string' && !value.startsWith('{{') ? meta.user(value)?.name : 'человек…'}</Button></PopoverTrigger>
          <PopoverContent width={260}><UserPicker value={value} onChange={(v) => onChange(v)} /></PopoverContent>
        </Popover>
      </span>
    );
  }
  if (field?.fieldType === 'relation') {
    const on = Array.isArray(value) && value[0] === '{{record.id}}';
    return <Button size="sm" variant={on ? 'secondary' : 'ghost'} onClick={() => onChange(on ? [] : ['{{record.id}}'])}>{on ? 'связать с этой записью ✓' : 'связать с этой записью'}</Button>;
  }
  const text = typeof value === 'string' ? value : value == null ? '' : String(value);
  // Outside focus the value reads as people see it («‹Название›»), the raw template shows only while editing.
  return (
    <span className={s.valueCol}>
      <TemplateInput text={text} pretty={text.includes('{{') ? prettyTemplate(text, meta, sourceTypeId) : text} onChange={onChange} />
      <span className={s.tokens}>{TOKENS.map((t) => <button key={t.label} type="button" className={s.token} onClick={() => onChange(field?.fieldType === 'date' ? t.v(titleField) : `${text}${t.v(titleField)}`)}>‹{t.label}›</button>)}</span>
    </span>
  );
}

function TemplateInput({ text, pretty, onChange }: { text: string; pretty: string; onChange: (v: string) => void }) {
  const [focus, setFocus] = useState(false);
  return <Input value={focus ? text : pretty} placeholder="Значение" onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} onChange={(e) => onChange(e.target.value)} />;
}

function StepEditor({ st, typeId, onChange }: { st: Step; typeId?: Id; onChange: (s: Step) => void }) {
  const meta = useMeta();
  const templates = useMailTemplates();
  const emails = typeId ? meta.fields(typeId).filter((f) => f.fieldType === 'email') : [];
  switch (st.type) {
    case 'setField': {
      const f = typeId ? meta.field(typeId, st.field) : undefined;
      return (
        <>
          <h3 className={s.inspTitle}>Изменить поле записи</h3>
          {/* A field the record's own actions manage (an order's status, its money) is not a step's to write; the server refuses it too. */}
          {typeId && <Row label="Поле"><FieldSelect typeId={typeId} value={st.field} filter={(x) => x.fieldType !== 'relation' && !x.options.managed} onChange={(k) => onChange({ ...st, field: k, value: '' })} /></Row>}
          <Row label="Значение"><ValueEditor field={f} value={st.value} sourceTypeId={typeId} onChange={(v) => onChange({ ...st, value: v })} /></Row>
        </>
      );
    }
    case 'createRecord': {
      const fields = meta.fields(st.typeId);
      // Relation fields of the new record that lead to the record of the scenario: the step links them (`linkTo`, decision 037).
      const links = typeId ? fields.filter((f) => {
        const r = f.fieldType === 'relation' ? meta.relation(f.options.relationId) : undefined;
        return !!r && (f.options.side === 'to' ? r.fromTypeId : r.toTypeId) === typeId;
      }) : [];
      return (
        <>
          <h3 className={s.inspTitle}>Создать запись</h3>
          <Row label="Раздел"><TypeSelect value={st.typeId} onChange={(id) => { const { linkTo: _drop, ...rest } = st; onChange({ ...rest, typeId: id, data: {} }); }} /></Row>
          {links.length > 0 && (
            <Row label="Связать с записью сценария">
              <Select size="sm" label="Связать с записью сценария" value={st.linkTo ?? ''}
                options={[{ value: '', label: 'Нет' }, ...links.map((f) => ({ value: f.key, label: f.name }))]}
                onChange={(k) => { const { linkTo: _drop, ...rest } = st; onChange(k ? { ...rest, linkTo: k } : rest); }} />
            </Row>
          )}
          {/* The field chosen above is filled by the link; its own row would be a second way to say the same (MiniMax review 31.10, P2-2). */}
          {fields.filter((f) => f.key !== st.linkTo).map((f) => (
            <Row key={f.id} label={f.name + (f.required ? ' *' : '')}>
              <ValueEditor field={f} value={st.data[f.key]} sourceTypeId={typeId} onChange={(v) => onChange({ ...st, data: { ...st.data, [f.key]: v } })} />
            </Row>
          ))}
        </>
      );
    }
    case 'wait': {
      const days = st.minutes >= 1440 && st.minutes % 1440 === 0;
      return (
        <>
          <h3 className={s.inspTitle}>Подождать</h3>
          <Row label="Сколько">
            <span className={s.opts}>
              <Input type="number" min={1} className={s.numInput} value={days ? st.minutes / 1440 : st.minutes / 60} onChange={(e) => onChange({ ...st, minutes: Math.max(1, Number(e.target.value)) * (days ? 1440 : 60) })} />
              <Segmented size="sm" label="Единица ожидания" value={days ? 'days' : 'hours'} items={[{ value: 'days', label: 'дней' }, { value: 'hours', label: 'часов' }]}
                onChange={(u) => onChange({ ...st, minutes: u === 'days' ? Math.max(1, Math.round(st.minutes / 1440)) * 1440 : Math.max(1, Math.round(st.minutes / 60)) * 60 })} />
            </span>
          </Row>
        </>
      );
    }
    case 'branch':
      return (
        <>
          <h3 className={s.inspTitle}>Если</h3>
          {typeId ? <FilterBuilder typeId={typeId} value={toFlat(st.if)} onChange={(ff) => onChange({ ...st, if: fromFlat(ff) ?? { and: [] } })} /> : <p className={s.hint}>Нужен раздел в запуске.</p>}
          <p className={s.hint}>Выполнено — идёт ветка «Да», иначе — «Нет».</p>
        </>
      );
    case 'stop':
      return (<><h3 className={s.inspTitle}>Остановить</h3><Row label="Причина"><Input value={st.reason} onChange={(e) => onChange({ ...st, reason: e.target.value })} /></Row></>);
    case 'webhook':
      return (
        <>
          <h3 className={s.inspTitle}>Вебхук</h3>
          <Row label="Адрес"><Input value={st.url} placeholder="https://example.com/hook" onChange={(e) => onChange({ ...st, url: e.target.value })} /></Row>
          <p className={s.hint}>Запись уходит POST-запросом в JSON. Не ответил 2xx — ещё две попытки, итог и причина — в журнале запусков. Адреса внутренней сети не вызываются.</p>
        </>
      );
    case 'command':
      return (<><h3 className={s.inspTitle}>Действие</h3><p className={s.hint}><Chip>{st.command}</Chip></p></>);
    case 'sendMail': {
      const names = [...templates.entries()];
      return (
        <>
          <h3 className={s.inspTitle}>Отправить письмо</h3>
          <Row label="Шаблон">
            <Menu>
              <MenuTrigger asChild><Button size="sm" variant="secondary" caret>{templates.get(st.template) ?? (st.template || 'Шаблон')}</Button></MenuTrigger>
              <MenuContent>{names.map(([code, title]) => <MenuItem key={code} onSelect={() => onChange({ ...st, template: code })}>{title}</MenuItem>)}</MenuContent>
            </Menu>
          </Row>
          {!names.length && <p className={s.hint}>Шаблонов пока нет: создайте письмо в разделе «Письма» — и оно появится здесь.</p>}
          <Row label="Кому">
            <Menu>
              <MenuTrigger asChild><Button size="sm" variant="secondary" caret>{st.toField ? (typeId ? meta.field(typeId, st.toField)?.name ?? st.toField : st.toField) : 'адрес'}</Button></MenuTrigger>
              <MenuContent>
                {emails.map((f) => <MenuItem key={f.key} onSelect={() => onChange({ ...st, toField: f.key, to: undefined })}>{f.name}</MenuItem>)}
                <MenuItem onSelect={() => onChange({ ...st, toField: undefined, to: '' })}>Другой адрес</MenuItem>
              </MenuContent>
            </Menu>
          </Row>
          {!st.toField && (
            <Row label="Адрес"><Input value={typeof st.to === 'string' ? st.to : ''} placeholder="почта@example.test" onChange={(e) => onChange({ ...st, to: e.target.value })} /></Row>
          )}
          <p className={s.hint}>{st.toField ? 'Письмо получит адрес из этого поля записи, с которой запустился сценарий.' : 'Письмо получит этот адрес.'}</p>
        </>
      );
    }
  }
}
